import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Nav from 'react-bootstrap/Nav';
import Table from 'react-bootstrap/Table';
import httpService from '../../../services/httpService';
import { TabSpinner, Th, AlignmentBadge, EmptyRow, formatDate } from './shared';

const SUB_TABS = [
    { key: 'resonance',  label: 'Which episodes resonate the most?' },
    { key: 'skills',     label: 'Skills by episode' },
    { key: 'takeaways',  label: 'Key takeaway performance' },
];

const EpisodesTab = ({ clubGuid }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSubTab, setActiveSubTab] = useState('resonance');

    useEffect(() => {
        httpService.get(`/dash/podcast-club/${clubGuid}/statistics/episodes`)
            .then((res) => setData(res.data))
            .catch((err) => setError(err.response?.data?.message || 'Error loading data'))
            .finally(() => setLoading(false));
    }, [clubGuid]);

    if (loading) return <TabSpinner />;
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (!data) return null;

    const resonance = data.episode_resonance || [];
    const skills = data.skills_by_episode || [];
    const takeawayPerf = data.key_takeaway_performance || [];

    return (
        <div>
            <Nav variant="tabs" activeKey={activeSubTab} onSelect={setActiveSubTab} className="mb-4">
                {SUB_TABS.map(({ key, label }) => (
                    <Nav.Item key={key}>
                        <Nav.Link eventKey={key}>{label}</Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            {/* Episode resonance */}
            <div style={{ display: activeSubTab === 'resonance' ? 'block' : 'none' }}>
                <p className="text-muted small mb-3">
                    Episodes ranked by total interactions. Sorted highest first — the episode at the top is the one that sparked the most listener activity.
                </p>
                <Table hover responsive size="sm" className="align-middle">
                    <thead className="table-light">
                        <tr>
                            <Th tip="The episode title as configured in the club wizard.">Episode</Th>
                            <Th tip="The scheduled start date for this episode within the club.">Publish date</Th>
                            <Th className="text-end" tip="Practices recorded + practice comments + discussion comments + reactions for this episode only.">Total interactions</Th>
                            <Th className="text-end" tip="Total interactions for this episode divided by the number of club subscribers. Normalises for club size so you can fairly compare episodes even as the audience grows.">Per listener</Th>
                            <Th tip="The 1–2 skills most frequently selected by listeners when recording a practice for this episode.">Top skills</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {resonance.length === 0
                            ? <EmptyRow cols={5} />
                            : resonance.map((ep) => (
                                <tr key={ep.episode_id}>
                                    <td className="fw-medium">{ep.title}</td>
                                    <td className="text-muted">{formatDate(ep.publish_date)}</td>
                                    <td className="text-end">{ep.total_interactions}</td>
                                    <td className="text-end">{ep.interactions_per_listener}</td>
                                    <td>
                                        {ep.top_skills?.length > 0
                                            ? ep.top_skills.map((s, i) => (
                                                <Badge key={i} bg="light" text="dark" className="me-1 border">{s}</Badge>
                                            ))
                                            : <span className="text-muted">—</span>
                                        }
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </Table>
            </div>

            {/* Skills by episode */}
            <div style={{ display: activeSubTab === 'skills' ? 'block' : 'none' }}>
                <p className="text-muted small mb-3">
                    Compares the skills the podcaster intended listeners to practice (suggested) with the skills they actually tagged. Helps surface gaps between content intent and listener behaviour.
                </p>
                <Table hover responsive size="sm" className="align-middle">
                    <thead className="table-light">
                        <tr>
                            <Th tip="The episode title.">Episode</Th>
                            <Th tip="The skills defined by the podcaster for the whole club (configured in club settings). These represent the intended learning outcomes.">Suggested skills</Th>
                            <Th tip="The distinct skills listeners actually chose when recording a practice for this episode. Drawn from the same club skill list as suggested skills.">Skills tagged by listeners</Th>
                            <Th tip="How closely listener behaviour matched the podcaster's intent. Strong: ≥70% of suggested skills were practiced. Partial: 40–69%. Divergent: <40% — listeners engaged with fewer skills than intended.">Alignment</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {skills.length === 0
                            ? <EmptyRow cols={4} />
                            : skills.map((ep, i) => (
                                <tr key={i}>
                                    <td className="fw-medium">{ep.episode_title}</td>
                                    <td>
                                        {ep.suggested_skills?.length > 0
                                            ? ep.suggested_skills.map((s, j) => (
                                                <Badge key={j} bg="light" text="dark" className="me-1 mb-1 border">{s}</Badge>
                                            ))
                                            : <span className="text-muted">—</span>
                                        }
                                    </td>
                                    <td>
                                        {ep.listener_skills?.length > 0
                                            ? ep.listener_skills.map((s, j) => (
                                                <Badge key={j} bg="light" text="dark" className="me-1 mb-1 border">{s}</Badge>
                                            ))
                                            : <span className="text-muted">None yet</span>
                                        }
                                    </td>
                                    <td><AlignmentBadge value={ep.alignment} /></td>
                                </tr>
                            ))
                        }
                    </tbody>
                </Table>
            </div>

            {/* Key takeaway performance */}
            <div style={{ display: activeSubTab === 'takeaways' ? 'block' : 'none' }}>
                <p className="text-muted small mb-3">
                    Which specific insights within each episode sparked the most listener action? Sorted highest first — use this to understand which formats and topics drive real behaviour change.
                </p>
                {takeawayPerf.length === 0 ? (
                    <p className="text-muted small">No takeaway data yet.</p>
                ) : (
                    takeawayPerf.map((ep, i) => (
                        ep.takeaways?.length > 0 && (
                            <div key={i} className="mb-4">
                                <div className="fw-medium small text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>
                                    {ep.episode_title}
                                </div>
                                <Table hover responsive size="sm" className="align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <Th tip="The key takeaway title as generated or customised for this episode.">Takeaway</Th>
                                            <Th className="text-end" tip="Number of practices listeners created for this takeaway.">Practices</Th>
                                            <Th className="text-end" tip="How many of those practices were marked as completed.">Completed</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ep.takeaways.map((t, j) => (
                                            <tr key={j}>
                                                <td>{t.title}</td>
                                                <td className="text-end">{t.practices_count}</td>
                                                <td className="text-end text-muted">{t.completed_count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )
                    ))
                )}
            </div>
        </div>
    );
};

export default EpisodesTab;
